import { Booking, BookingColumn } from "@api";
import { Download, PictureAsPdf, TableChart } from "@mui/icons-material";
import { Alert, Button, Checkbox, Collapse, Divider, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import React, { useState } from "react";
import { parseError } from "sprungkraft/helper/helper_functions";
import { useStore } from "sprungkraft/state/state";

const COLUMN_LABELS: Record<BookingColumn, string> = {
    [BookingColumn.KursNr]: 'Kurs-Nr',
    [BookingColumn.KursId]: 'Kurs-ID',
    [BookingColumn.Kurszeitraum]: 'Kurszeitraum',
    [BookingColumn.Anrede]: 'Anrede',
    [BookingColumn.Nachname]: 'Nachname',
    [BookingColumn.Vorname]: 'Vorname',
    [BookingColumn.Geburtsdatum]: 'Geburtsdatum',
    [BookingColumn.Krankenkasse]: 'Krankenkasse',
    [BookingColumn.Email]: 'E-Mail',
    [BookingColumn.Telefon]: 'Telefon',
    [BookingColumn.Kursgebuehr]: 'Kursgebühr',
    [BookingColumn.Buchungszeitpunkt]: 'Buchungszeitpunkt',
    [BookingColumn.Storno]: 'Storno',
    [BookingColumn.Rueckerstattung]: 'Rückerstattung',
    [BookingColumn.InfosTrainer]: 'Infos Trainer',
    [BookingColumn.Teilnahmebeschein]: 'Teilnahmebeschein.',
    [BookingColumn.Kursnewsletter]: 'Kursnewsletter',
    [BookingColumn.Strasse]: 'Straße',
    [BookingColumn.Hausnummer]: 'Hausnummer',
    [BookingColumn.Plz]: 'PLZ',
    [BookingColumn.Ort]: 'Ort',
};

const ALL_COLUMNS = Object.values(BookingColumn);

interface BookingsContentProps {
    bookings: Booking[],
}

const BookingsContent = ({ bookings }: BookingsContentProps) => {
    const { api: { bookingApi } } = useStore();
    const [error, setError] = useState<string | null>(null);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<Set<BookingColumn>>(new Set(ALL_COLUMNS));
    const [showColumnPicker, setShowColumnPicker] = useState(false);

    const toggleColumn = (col: BookingColumn) => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            next.has(col) ? next.delete(col) : next.add(col);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedColumns(selectedColumns.size === ALL_COLUMNS.length ? new Set() : new Set(ALL_COLUMNS));
    };

    const activeColumns = ALL_COLUMNS.filter(c => selectedColumns.has(c));
    const columnsParam = activeColumns.length < ALL_COLUMNS.length ? activeColumns : undefined;

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const onExportBookings = (all: boolean) => {
        bookingApi.getBookingsXLSX({
            onlyNewOnes: !all,
        }).subscribe({
            next: (blob) => downloadBlob(blob, 'buchungsliste.xlsx'),
            error: (err) => setError(parseError(err))
        });
    };

    const onExportDateRangeXLSX = () => {
        bookingApi.getBookingsXLSX({
            onlyNewOnes: false,
            fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
            toDate: toDate ? toDate.format('YYYY-MM-DD') : undefined,
            columns: columnsParam,
        }).subscribe({
            next: (blob) => downloadBlob(blob, 'buchungsliste.xlsx'),
            error: (err) => setError(parseError(err))
        });
    };

    const onExportPDF = () => {
        bookingApi.getBookingsPDF({
            fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
            toDate: toDate ? toDate.format('YYYY-MM-DD') : undefined,
            columns: columnsParam,
        }).subscribe({
            next: (blob) => downloadBlob(blob, 'buchungsliste.pdf'),
            error: (err) => setError(parseError(err))
        });
    };

    const dateRangeLabel = fromDate || toDate
        ? `(${fromDate ? fromDate.format('DD.MM.YYYY') : '...'} - ${toDate ? toDate.format('DD.MM.YYYY') : '...'})`
        : '';

    return (
        <Stack alignItems="flex-start" spacing={2}>
            <Typography>Es sind {bookings.length} Buchungen hinterlegt</Typography>

            {/* Quick exports (no date filter, no column selection) */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={() => onExportBookings(false)} startIcon={<TableChart />}>
                    Neue Buchungen exportieren
                </Button>
                <Button onClick={() => onExportBookings(true)} startIcon={<TableChart />}>
                    Alle Buchungen exportieren
                </Button>
            </Stack>

            <Divider flexItem />

            {/* Date-range export with column selection */}
            <Typography variant="subtitle2">Buchungsliste für Zeitraum herunterladen</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <DatePicker
                    label="Von (Tag/Monat/Jahr)"
                    value={fromDate}
                    onChange={setFromDate}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small' } }}
                />
                <Typography>bis</Typography>
                <DatePicker
                    label="Bis (Tag/Monat/Jahr)"
                    value={toDate}
                    onChange={setToDate}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small' } }}
                />
            </Stack>

            {/* Column picker */}
            <Stack spacing={0.5} alignItems="flex-start">
                <Button size="small" variant="text" onClick={() => setShowColumnPicker(v => !v)}>
                    Spalten auswählen ({selectedColumns.size}/{ALL_COLUMNS.length})
                </Button>
                <Collapse in={showColumnPicker}>
                    <Stack spacing={0.5}>
                        <FormControlLabel
                            label={<Typography variant="body2"><strong>Alle</strong></Typography>}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedColumns.size === ALL_COLUMNS.length}
                                    indeterminate={selectedColumns.size > 0 && selectedColumns.size < ALL_COLUMNS.length}
                                    onChange={toggleAll}
                                />
                            }
                        />
                        <FormGroup row>
                            {ALL_COLUMNS.map((col: BookingColumn) => (
                                <FormControlLabel
                                    key={col}
                                    label={<Typography variant="body2">{COLUMN_LABELS[col]}</Typography>}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedColumns.has(col)}
                                            onChange={() => toggleColumn(col)}
                                        />
                                    }
                                    sx={{ width: 180 }}
                                />
                            ))}
                        </FormGroup>
                    </Stack>
                </Collapse>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                    variant="contained"
                    onClick={onExportDateRangeXLSX}
                    startIcon={<Download />}
                >
                    Als Excel herunterladen {dateRangeLabel}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={onExportPDF}
                    startIcon={<PictureAsPdf />}
                >
                    Als PDF herunterladen {dateRangeLabel}
                </Button>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        </Stack>
    );
};

export default BookingsContent;
